import axios from 'axios';

// API基础配置
const API_BASE_URL = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === 'production' 
    ? 'https://api.snagyourtee.com' 
    : 'http://localhost:3000'
);

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: true, // 重要：发送 cookies 以支持 Better-auth
  headers: {
    'Content-Type': 'application/json',
  },
});

// 在开发环境下显示当前API URL，方便调试
if (process.env.NODE_ENV === 'development') {
  console.log(`🔗 API Base URL: ${API_BASE_URL}`);
}

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // API request logging disabled in production
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    // API response logging disabled in production
    return response;
  },
  (error) => {
    console.error('API Response Error:', error);
    
    // 统一错误处理
    if (error.response) {
      // 服务器响应了错误状态码
      const { status, data } = error.response;
      console.error(`API Error ${status}:`, data);
    } else if (error.request) {
      // 请求发出但没有收到响应
      console.error('Network Error: No response received');
    } else {
      // 其他错误
      console.error('Request Setup Error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// 数据类型定义
export interface MonitoringRequestData {
  courseIds: number[];
  preferredDates: string[];
  startTime: string;
  endTime: string;
  numberOfPlayers: number;
  contactPhone: string;
  contactEmail: string;
  notificationMethod: 'sms' | 'email' | 'both';
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface GolfCourse {
  id: number;
  name: string;
  location?: string;
  is_active: boolean;
  last_updated: string;
  api_source?: string;
  booking_url?: string;           // 预订链接
  // Detailed course information for CoursesPage
  city?: string;
  holes?: number;
  length_yards?: number;
  par?: number;
  course_rating?: number;
  slope_rating?: number;
  image_url?: string;
  description?: string;
  // Booking configuration for WaitlistPage
  booking_advance_days?: number;  // 可提前预订天数
  booking_open_time?: string;     // 每日开放预订时间(UTC)
}

// 球场详情接口（包含完整的展示信息）
export interface GolfCourseDetails {
  // 基础信息（来自 golf_courses_v2）
  id: number;
  name: string;
  location?: string;
  api_type: string;
  is_active: boolean;
  booking_advance_days?: number;
  booking_open_time?: string;
  created_at?: string;
  updated_at?: string;
  
  // 详细信息（来自 golf_course_details）
  holes?: number;
  length_yards?: number;
  par?: number;
  course_rating?: number;
  slope_rating?: number;
  course_type?: string;
  is_public?: boolean;
  description?: string;
  facilities?: string[];
  features?: string[];
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  
  // 图片信息（来自 golf_course_images）
  images?: GolfCourseImage[];
  image_url?: string; // 主图片URL
}

// 球场图片接口
export interface GolfCourseImage {
  id: number;
  course_id: number;
  url: string;
  alt_text?: string;
  display_order: number;
  is_primary: boolean;
  created_at?: string;
}

export interface RemovedCourse {
  id: number;
  name: string;
  reason: string;
  bookingDays: number;
}

export interface MonitoringRequest {
  id: number;
  course_ids: number[];
  preferred_dates: string[];
  start_time: string;
  end_time: string;
  number_of_players: number;
  contact_phone: string | null;
  contact_email: string;
  notification_method: 'sms' | 'email' | 'both';
  is_active: boolean;
  created_at: string;
  expires_at: string;
  // Additional fields for booking window validation
  removedCourses?: RemovedCourse[];
  warningMessage?: string;
}

// Tee Times相关接口定义
export interface TeeTimeSlot {
  time: string;
  price: number;
  availableSpots: number;
  maxPlayers?: number;
  courseId?: number;
  courseName?: string;
}

export interface CourseAvailability {
  courseId: number;
  courseName: string;
  teeTimeSlots: TeeTimeSlot[];
  totalSlots: number;
  lastUpdated: string;
}

export interface TeeTimesResponse {
  success: boolean;
  data: CourseAvailability[];
  timestamp: string;
  params: {
    date: string;
    courseIds: number[];
    timeRange: {
      startHour: number;
      endHour: number;
    };
    numberOfPlayers: number;
  };
}

export interface TeeTimesParams {
  date?: string;
  courseIds?: number[];
  startHour?: number;
  endHour?: number;
  numberOfPlayers?: number;
}

// Admin Golf Course interfaces
export interface AdminGolfCourse {
  id: number;
  name: string;
  location: string;
  description?: string;
  images?: string[] | GolfCourseImage[];
  api_type: 'cps' | 'chronogolf';
  api_course_id: string;
  api_config: {
    [key: string]: any;
  };
  booking_url?: string;
  booking_advance_days?: number;
  booking_open_time?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Golf course details from golf_course_details table
  holes?: number;
  length_yards?: number;
  par?: number;
  course_rating?: number;
  slope_rating?: number;
  course_type?: string;
  is_public?: boolean;
  facilities?: string[];
  features?: string[];
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
}

export interface CreateGolfCourseData {
  name: string;
  location: string;
  description?: string;
  images?: string[];
  api_type: 'cps' | 'chronogolf';
  api_config: {
    [key: string]: any;
  };
  booking_url?: string;
  booking_advance_days?: number;
  booking_open_time?: string;
}

export interface UpdateGolfCourseData extends Partial<CreateGolfCourseData> {}

export interface GolfCourseStats {
  totalCourses: number;
  activeCourses: number;
  apiTypeDistribution: {
    cps: number;
    chronogolf: number;
  };
  mostMonitoredCourses: Array<{
    courseId: number;
    courseName: string;
    monitoringCount: number;
  }>;
}

export interface AdminGolfCoursesResponse {
  courses: AdminGolfCourse[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// API方法
export const apiService = {
  // 获取球场列表
  async getGolfCourses(): Promise<GolfCourse[]> {
    try {
      const response = await api.get<ApiResponse<GolfCourse[]>>('/api/golf-courses');
      return response.data.data || [];
    } catch (error) {
      console.error('Failed to fetch golf courses:', error);
      // 返回默认数据作为fallback (包含所有5个球场)
      return [
        { id: 1, name: 'Langara Golf Course', location: 'Vancouver, BC', is_active: true, last_updated: new Date().toISOString(), api_source: 'vancouver' },
        { id: 2, name: 'Fraserview Golf Course', location: 'Vancouver, BC', is_active: true, last_updated: new Date().toISOString(), api_source: 'vancouver' },
        { id: 3, name: 'McCleery Golf Course', location: 'Vancouver, BC', is_active: true, last_updated: new Date().toISOString(), api_source: 'vancouver' },
        { id: 4, name: 'Burnaby Mountain Golf Course', location: 'Burnaby, BC', is_active: true, last_updated: new Date().toISOString(), api_source: 'burnaby' },
        { id: 5, name: 'Riverway Golf Course', location: 'Burnaby, BC', is_active: true, last_updated: new Date().toISOString(), api_source: 'burnaby' }
      ];
    }
  },

  // 获取球场列表（用于CoursesPage展示，包含完整展示数据）
  async getGolfCoursesList(): Promise<GolfCourse[]> {
    try {
      const response = await api.get<ApiResponse<GolfCourse[]>>('/api/golf-courses/list');
      return response.data.data || [];
    } catch (error) {
      console.error('Failed to fetch golf courses list:', error);
      // 返回空数组，让CoursesPage处理错误状态
      return [];
    }
  },

  // 创建监控请求
  async createMonitoringRequest(data: MonitoringRequestData): Promise<MonitoringRequest> {
    const response = await api.post<ApiResponse<MonitoringRequest>>('/api/monitoring-requests', data);
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to create monitoring request');
    }
    
    return response.data.data!;
  },

  // 获取监控请求列表
  async getMonitoringRequests(): Promise<MonitoringRequest[]> {
    const response = await api.get<ApiResponse<MonitoringRequest[]>>('/api/monitoring-requests');
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch monitoring requests');
    }
    
    return response.data.data || [];
  },

  // 健康检查
  async healthCheck(): Promise<any> {
    const response = await api.get('/health');
    return response.data;
  },

  // Tee Times相关方法
  
  // 获取今天的tee times
  async fetchTeeTimesToday(params?: Omit<TeeTimesParams, 'date'>): Promise<CourseAvailability[]> {
    const today = new Date().toISOString().split('T')[0];
    return this.fetchTeeTimesByDate({ ...params, date: today });
  },

  // 获取指定日期的tee times
  async fetchTeeTimesByDate(params: TeeTimesParams = {}): Promise<CourseAvailability[]> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.date) queryParams.append('date', params.date);
      if (params.courseIds && params.courseIds.length > 0) {
        queryParams.append('courseIds', params.courseIds.join(','));
      }
      if (params.startHour !== undefined) queryParams.append('startHour', params.startHour.toString());
      if (params.endHour !== undefined) queryParams.append('endHour', params.endHour.toString());
      if (params.numberOfPlayers !== undefined) queryParams.append('numberOfPlayers', params.numberOfPlayers.toString());
      
      const response = await api.get<TeeTimesResponse>(`/api/tee-times?${queryParams.toString()}`);
      
      if (!response.data.success) {
        throw new Error('Failed to fetch tee times');
      }
      
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch tee times:', error);
      // 返回空数组作为fallback
      return [];
    }
  },

  // 获取特定球场的tee times
  async fetchTeeTimesByCourse(courseId: number, params?: Omit<TeeTimesParams, 'courseIds'>): Promise<CourseAvailability[]> {
    return this.fetchTeeTimesByDate({ ...params, courseIds: [courseId] });
  },

  // 获取球场预订链接 (从数据库动态获取)
  async getCourseBookingUrl(courseId: number): Promise<string> {
    try {
      const response = await api.get(`/api/golf-courses/${courseId}/booking-url`);
      if (response.data.success) {
        return response.data.data.bookingUrl;
      }
      throw new Error('Failed to get booking URL');
    } catch (error) {
      console.error(`Failed to get booking URL for course ${courseId}:`, error);
      // 返回默认URL作为fallback
      return 'https://golfvancouver.cps.golf/onlineresweb/search-teetime';
    }
  },

  // Admin Golf Course Management APIs
  
  // 获取管理员球场列表
  async getAdminGolfCourses(params?: {
    page?: number;
    limit?: number;
    api_type?: string;
    is_active?: boolean;
  }): Promise<AdminGolfCoursesResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.api_type) queryParams.append('api_type', params.api_type);
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());

    const response = await api.get<ApiResponse<AdminGolfCoursesResponse>>(`/api/admin/golf-courses?${queryParams.toString()}`);
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch admin golf courses');
    }
    
    return response.data.data!;
  },

  // 获取单个球场详情
  async getAdminGolfCourse(id: number): Promise<AdminGolfCourse> {
    const response = await api.get<ApiResponse<AdminGolfCourse>>(`/api/admin/golf-courses/${id}`);
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch golf course');
    }
    
    return response.data.data!;
  },

  // 创建新球场
  async createGolfCourse(data: CreateGolfCourseData): Promise<AdminGolfCourse> {
    const response = await api.post<ApiResponse<AdminGolfCourse>>('/api/admin/golf-courses', data);
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to create golf course');
    }
    
    return response.data.data!;
  },

  // 更新球场
  async updateGolfCourse(id: number, data: UpdateGolfCourseData): Promise<AdminGolfCourse> {
    const response = await api.put<ApiResponse<AdminGolfCourse>>(`/api/admin/golf-courses/${id}`, data);
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update golf course');
    }
    
    return response.data.data!;
  },

  // 删除球场
  async deleteGolfCourse(id: number): Promise<void> {
    const response = await api.delete<ApiResponse>(`/api/admin/golf-courses/${id}`);
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to delete golf course');
    }
  },

  // 切换球场状态
  async toggleGolfCourseStatus(id: number): Promise<AdminGolfCourse> {
    const response = await api.patch<ApiResponse<AdminGolfCourse>>(`/api/admin/golf-courses/${id}/toggle`);
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to toggle golf course status');
    }
    
    return response.data.data!;
  },

  // 验证API配置
  async validateGolfCourseConfig(data: { api_type: string; api_config: any }): Promise<{ valid: boolean; message: string }> {
    const response = await api.post<ApiResponse<{ valid: boolean; message: string }>>('/api/admin/golf-courses/validate-config', data);
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to validate configuration');
    }
    
    return response.data.data!;
  },

  // 测试API连接
  async testGolfCourseApi(id: number): Promise<{ success: boolean; message: string; data?: any }> {
    const response = await api.post<ApiResponse<{ success: boolean; message: string; data?: any }>>(`/api/admin/golf-courses/${id}/test-api`);
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to test API connection');
    }
    
    return response.data.data!;
  },

  // 获取球场统计信息
  async getGolfCourseStats(): Promise<GolfCourseStats> {
    const response = await api.get<ApiResponse<GolfCourseStats>>('/api/admin/golf-courses/stats');
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch golf course stats');
    }
    
    return response.data.data!;
  },

  // 清除球场缓存
  async clearGolfCourseCache(): Promise<void> {
    const response = await api.post<ApiResponse>('/api/admin/golf-courses/cache/clear');
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to clear cache');
    }
  },

  // 获取球场详情
  async getGolfCourseDetails(courseId: number): Promise<GolfCourseDetails> {
    try {
      const response = await api.get<ApiResponse<GolfCourseDetails>>(`/api/golf-courses/${courseId}`);
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to fetch course details');
      }
      
      return response.data.data!;
    } catch (error) {
      console.error(`Failed to fetch details for course ${courseId}:`, error);
      throw error;
    }
  },

  // 上传球场图片到Supabase Storage
  async uploadGolfCourseImage(courseId: number, file: File): Promise<ApiResponse<{imageId: number, url: string}>> {
    // Convert file to base64
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64Data = reader.result as string;
          
          const response = await api.post<ApiResponse<{
            id: number;
            url: string;
            filePath: string;
            fileSize: number;
          }>>(`/api/admin/golf-courses/${courseId}/upload-image`, {
            fileData: base64Data,
            filename: file.name,
            contentType: file.type
          });
          
          if (!response.data.success) {
            throw new Error(response.data.error || 'Failed to upload image');
          }
          
          const uploadData = response.data.data!;
          
          const result: ApiResponse<{imageId: number, url: string}> = {
            success: true,
            data: {
              imageId: uploadData.id,
              url: uploadData.url
            }
          };
          
          resolve(result);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      
      reader.readAsDataURL(file);
    });
  },

  // 获取球场图片列表
  async getGolfCourseImages(courseId: number): Promise<ApiResponse<GolfCourseImage[]>> {
    const response = await api.get<ApiResponse<GolfCourseImage[]>>(`/api/admin/golf-courses/${courseId}/images`);
    return response.data;
  },

  // 删除球场图片
  async deleteGolfCourseImage(imageId: number): Promise<ApiResponse> {
    const response = await api.delete<ApiResponse>(`/api/admin/golf-courses/delete-image/${imageId}`);
    return response.data;
  },

  // Reorder golf course images
  async reorderGolfCourseImages(imageIds: number[]): Promise<ApiResponse> {
    const response = await api.patch<ApiResponse>('/api/admin/golf-courses/reorder-images', {
      imageIds
    });
    return response.data;
  }
};

export default api;